/**
 * LoginScreen.js — login / register / password-reset modal.
 *
 * Handles four modes:
 * - 'login'    — email + password sign in
 * - 'register' — create a new account (name, email, password)
 * - 'forgot'   — request a password-reset email
 * - 'reset'    — set a new password using a token from the reset email
 *
 * The mode is determined by the `mode` state variable. On mount, the component
 * checks the URL for a `?token=…` query parameter (used by the reset-password
 * email link) and automatically switches to 'reset' mode if present.
 *
 * Role in the app: shown by App.js when no account is logged in. On
 * successful login/register, calls `onLogin(account)` which triggers a session
 * fetch in App.js to load teams and scenarios.
 *
 * Props:
 * - onLogin:  (account) => void — called after successful login or registration
 * - onClose:  () => void — optional, shows a close (×) button to dismiss the modal
 * - onOpenGuides: () => void — optional, the footer's way into the app's own
 *   guides. This screen used to carry a plain link to the backend website: the
 *   one place in the app that handed you OUT of it, on the very first screen a
 *   visitor sees. The guides are in-app now (HelpModal → GuideReader).
 *
 * Every string here goes through t() — the rest of the app is English /
 * Portuguese / Spanish, and the door to it used to be English-only.
 */
import { useState, useEffect } from 'react';
import { authAPI } from './api';
import { useI18n } from './i18n';

/**
 * LoginScreen component.
 *
 * @param {Object}   props
 * @param {Function} props.onLogin - Callback invoked with the account object after auth.
 * @returns {JSX.Element} The login screen markup.
 */
function LoginScreen({ onLogin, onClose, onOpenGuides }) {
  const { t } = useI18n();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // ── Check URL for reset-password token on mount ──────────────────

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token && window.location.pathname.includes('reset')) {
      setResetToken(token);
      setMode('reset');
    }
  }, []);

  // ── Auth handlers ────────────────────────────────────────────────

  /** Handle sign-in form submission. */
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email) { setError(t('login.emailRequired')); return; }
    setError(''); setLoading(true);
    try {
      const data = await authAPI.login(email, password);
      onLogin(data.account);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  /** Handle registration form submission. */
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError(t('login.nameRequired')); return; }
    if (!email) { setError(t('login.emailRequired')); return; }
    setError(''); setLoading(true);
    try {
      const data = await authAPI.register(name.trim(), email, password);
      onLogin(data.account);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  /** Handle forgot-password form submission (sends reset email). */
  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) { setError(t('login.emailRequired')); return; }
    setError(''); setLoading(true);
    try {
      const data = await authAPI.forgotPassword(email);
      setMessage(data.message || t('login.resetSent'));
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  /** Handle reset-password form submission (uses token from email link). */
  const handleReset = async (e) => {
    e.preventDefault();
    if (!newPassword) { setError(t('login.pwRequired')); return; }
    if (newPassword.length < 6) { setError(t('login.pwShort')); return; }
    setError(''); setLoading(true);
    try {
      const data = await authAPI.resetPassword(resetToken, newPassword);
      onLogin(data.account);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  // ── Shared inline styles ─────────────────────────────────────────

  const inputStyle = {
    width: '100%', padding: '0.6rem', background: '#0f172a', border: '1px solid #334155',
    borderRadius: '6px', color: '#f8fafc', fontSize: '0.9rem',
  };
  const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#cbd5e1', marginBottom: '0.4rem' };
  /** Button style factory — dims the button and shows 'wait' cursor while loading. */
  const btnStyle = (bg) => ({
    width: '100%', padding: '0.7rem', background: bg, color: '#fff',
    border: 'none', borderRadius: '6px', fontWeight: 700, fontSize: '0.95rem',
    cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1,
  });

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(2, 6, 23, 0.72)', color: '#f1f5f9',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      padding: '12px', boxSizing: 'border-box',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        background: '#1e293b', padding: '1.6rem', borderRadius: '12px',
        boxShadow: '0 18px 48px rgba(0,0,0,0.55)', width: '100%', maxWidth: '380px',
        position: 'relative',
        // Never taller than the visible viewport: on short landscape screens
        // the card shrinks to fit and scrolls internally instead of
        // overflowing (dvh-backed var keeps it above the browser chrome).
        maxHeight: 'calc(var(--kv-viewport-h) - 24px)', overflowY: 'auto',
      }}>
        {/* ── Close button (modal mode only) ── */}
        {onClose && (
          <button onClick={onClose} style={{
            position: 'absolute', top: '0.75rem', right: '0.75rem',
            background: 'none', border: 'none', color: '#64748b',
            fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1,
            padding: '0.2rem 0.5rem', borderRadius: '4px',
          }} title={t('login.close')}>&times;</button>
        )}

        {/* ── Logo ── */}
        <div style={{ textAlign: 'center', margin: '0 0 1.5rem 0' }}>
          <img src={`${process.env.PUBLIC_URL}/logo-login.png`} alt="KickViz" style={{ height: '56px', width: 'auto' }} />
        </div>

        {/* ── Error / success banners ── */}
        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.15)', border: '1px solid #ef4444',
            color: '#fca5a5', padding: '0.6rem 1rem', borderRadius: '6px',
            marginBottom: '1rem', fontSize: '0.85rem',
          }}>{error}</div>
        )}
        {message && (
          <div style={{
            background: 'rgba(34,197,94,0.15)', border: '1px solid #22c55e',
            color: '#86efac', padding: '0.6rem 1rem', borderRadius: '6px',
            marginBottom: '1rem', fontSize: '0.85rem',
          }}>{message}</div>
        )}

        {/* ── Forgot-password mode ── */}
        {mode === 'forgot' ? (
          <form onSubmit={handleForgot}>
            <h2 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>{t('login.forgotTitle')}</h2>
            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              {t('login.forgotBody')}
            </p>
            <div style={{ marginBottom: '1rem' }}>
              {/* The labels used to be decoration: a <label> with no 'for' is
                  not a tap target and a screen reader cannot read it. Tapping
                  the word now focuses the field — one less aim on a phone. */}
              <label style={labelStyle} htmlFor="kv-forgot-email">{t('login.email')}</label>
              <input id="kv-forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" style={inputStyle} required />
            </div>
            <button type="submit" disabled={loading} style={btnStyle('#0284c7')}>
              {loading ? t('login.sending') : t('login.sendLink')}
            </button>
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button onClick={() => { setMode('login'); setError(''); setMessage(''); }}
                style={{ background: 'none', border: 'none', color: '#0284c7', fontSize: '0.85rem', cursor: 'pointer' }}>
                {t('login.back')}
              </button>
            </div>
          </form>
        ) : mode === 'reset' ? (
          /* ── Reset-password mode ── */
          <form onSubmit={handleReset}>
            <h2 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>{t('login.resetTitle')}</h2>
            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              {t('login.resetBody')}
            </p>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle} htmlFor="kv-reset-password">{t('login.newPassword')}</label>
              <input id="kv-reset-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="••••••••" style={inputStyle} minLength="6" required />
            </div>
            <button type="submit" disabled={loading} style={btnStyle('#0284c7')}>
              {loading ? t('login.resetting') : t('login.reset')}
            </button>
          </form>
        ) : (
          /* ── Login / Register mode ── */
          <>
            {/* Mode toggle: Sign In | Register */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
              <button onClick={() => { setMode('login'); setError(''); setMessage(''); }}
                style={{
                  flex: 1, padding: '0.5rem', border: 'none', borderRadius: '6px',
                  fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                  background: mode === 'login' ? '#0284c7' : '#334155', color: '#fff',
                }}>{t('login.signIn')}</button>
              <button onClick={() => { setMode('register'); setError(''); setMessage(''); }}
                style={{
                  flex: 1, padding: '0.5rem', border: 'none', borderRadius: '6px',
                  fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                  background: mode === 'register' ? '#4f46e5' : '#334155', color: '#fff',
                }}>{t('login.register')}</button>
            </div>

            {mode === 'login' ? (
              /* ── Login form ── */
              <form onSubmit={handleLogin}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle} htmlFor="kv-login-email">{t('login.email')}</label>
                  <input id="kv-login-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com" style={inputStyle} required />
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={labelStyle} htmlFor="kv-login-password">{t('login.password')}</label>
                  <input id="kv-login-password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" style={inputStyle} />
                </div>
                <div style={{ textAlign: 'right', marginBottom: '1.25rem' }}>
                  <button type="button" onClick={() => { setMode('forgot'); setError(''); setMessage(''); }}
                    style={{ background: 'none', border: 'none', color: '#0284c7', fontSize: '0.8rem', cursor: 'pointer' }}>
                    {t('login.forgot')}
                  </button>
                </div>
                <button type="submit" disabled={loading} style={btnStyle('#0284c7')}>
                  {loading ? t('login.signingIn') : t('login.signIn')}
                </button>
              </form>
            ) : (
              /* ── Register form ── */
              <form onSubmit={handleRegister}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle} htmlFor="kv-reg-name">{t('login.orgName')}</label>
                  <input id="kv-reg-name" type="text" value={name} onChange={e => setName(e.target.value)}
                    placeholder={t('login.orgPlaceholder')} style={inputStyle} required />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle} htmlFor="kv-reg-email">{t('login.email')}</label>
                  <input id="kv-reg-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com" style={inputStyle} required />
                </div>
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={labelStyle} htmlFor="kv-reg-password">{t('login.password')}</label>
                  <input id="kv-reg-password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" style={inputStyle} />
                </div>
                <button type="submit" disabled={loading} style={btnStyle('#4f46e5')}>
                  {loading ? t('login.creating') : t('login.create')}
                </button>
              </form>
            )}
          </>
        )}

        {/* ── Footer: the app's own guides + copyright ──
            This used to be a plain link to the backend website — on the very
            first screen a visitor meets, the one control in the whole app whose
            job was to navigate you OUT of it. The guides live in the app now, so
            the footer offers those instead and nobody has to leave to learn
            what the tool does. */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: '#64748b' }}>
          {onOpenGuides && (
            <>
              <button type="button" onClick={onOpenGuides}
                style={{ background: 'none', border: 'none', color: '#0284c7', fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}>
                {t('login.guides')}
              </button>
              <span style={{ margin: '0 0.5rem' }}>·</span>
            </>
          )}
          KickViz Platform © 2026
        </div>
      </div>
    </div>
  );
}

export default LoginScreen;
